﻿import React, { useContext, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ListSubheader from "@material-ui/core/ListSubheader";
import Divider from "@material-ui/core/Divider";
import { Badge, Menu, MenuItem } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import LaunchIcon from "@material-ui/icons/Launch";

import { i18n } from "../translate/i18n";
import { ConnectionsContext } from "../context/Connections/ConnectionsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { Can } from "../components/Can";
import api from "../services/api";

const useStyles = makeStyles(() => ({
  listItem: {
    paddingLeft: 25,
    paddingRight: 16
  },
  listItemIcon: {
    color: "rgba(0, 0, 0, 0.54)",
    display: "inline-flex",
    justifyContent: "flex-start",
    minWidth: 40,
    flexShrink: 0
  },
  listItemIconClosed: {
    minWidth: 55
  },
  emojiIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "initial",
    fontSize: 21,
    height: 24,
    lineHeight: "24px",
    opacity: 1,
    width: 24
  },
  listSubheader: {
    paddingLeft: 80
  },
  secondaryIcon: {
    color: "rgba(0, 0, 0, 0.38)",
    marginLeft: "auto"
  },
  categoryColor: {
    borderRadius: 999,
    display: "inline-block",
    height: 10,
    marginRight: 8,
    width: 10
  }
}));

const MenuEmoji = ({ children, label }) => {
  const classes = useStyles();

  return (
    <span className={classes.emojiIcon} role="img" aria-label={label}>
      {children}
    </span>
  );
};

export const menuPermissionOptions = [
  { value: "dashboard", label: "📊 Dashboard" },
  { value: "connections", label: "🔗 Conexiones" },
  { value: "tickets", label: "💬 Chats" },
  { value: "contacts", label: "👥 Contactos" },
  { value: "quickAnswers", label: "⚡ Respuestas rápidas" },
  { value: "reminders", label: "⏰ Recordatorios" },
  { value: "agenda", label: "📅 Agenda" },
  { value: "orders", label: "🛒 Pedidos" },
  { value: "processes", label: "📃 Procesos" },
  { value: "streaming", label: "📺 Streaming" },
  { value: "campaigns", label: "📢 Campañas" },
  { value: "internalChat", label: "🗨️ Chat Interno" },
  { value: "funnels", label: "📈 Embudos" },
  { value: "chatbot", label: "🤖 Chatbot" },
  { value: "ai", label: "🧠 IA" },
  { value: "sidepanelApps", label: "📱 Apps" },
  { value: "workspaceData", label: "🧱 Base de datos" },
  { value: "reseller", label: "🤝 Panel Reseller" },
  { value: "users", label: "👤 Usuarios", adminOnly: true },
  { value: "queues", label: "🏷️ Áreas", adminOnly: true },
  { value: "labels", label: "🏷️ Etiquetas", adminOnly: true },
  { value: "webhooks", label: "🔌 Webhooks", adminOnly: true },
  { value: "menu", label: "☰ Menú", adminOnly: true }
];

export const hasMenuPermission = (user, permission) => {
  if (!permission || ["superadmin", "owner"].includes(user?.profile)) return true;
  if (permission === "menu" && !["admin", "superadmin", "owner"].includes(user?.profile)) {
    return false;
  }
  if (permission === "settings") return false;

  let companyPermissions = user?.company?.menuPermissions;
  let userPermissions = user?.menuPermissions;

  if (typeof companyPermissions === "string" && companyPermissions.trim()) {
    try {
      companyPermissions = JSON.parse(companyPermissions);
    } catch {
      companyPermissions = null;
    }
  }

  if (typeof userPermissions === "string" && userPermissions.trim()) {
    try {
      userPermissions = JSON.parse(userPermissions);
    } catch {
      userPermissions = null;
    }
  }

  if (
    permission === "reseller" &&
    (!Array.isArray(companyPermissions) || !companyPermissions.includes(permission))
  ) {
    return false;
  }

  if (Array.isArray(companyPermissions) && !companyPermissions.includes(permission)) {
    return false;
  }

  if (!Array.isArray(userPermissions)) return true;

  return userPermissions.includes(permission);
};

function ListItemLink(props) {
  const {
    drawerOpen,
    icon,
    primary,
    to,
    className,
    externalUrl,
    secondaryIcon
  } = props;
  const classes = useStyles();

  const renderLink = React.useMemo(
    () =>
      React.forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
      )),
    [to]
  );

  return (
    <li>
      <ListItem
        button
        component={externalUrl ? "a" : renderLink}
        href={externalUrl || undefined}
        target={externalUrl ? "_blank" : undefined}
        rel={externalUrl ? "noopener noreferrer" : undefined}
        className={`${classes.listItem} ${className || ""}`}
      >
        {icon ? (
          <ListItemIcon
            className={`${classes.listItemIcon} ${
              drawerOpen ? "" : classes.listItemIconClosed
            }`}
          >
            {icon}
          </ListItemIcon>
        ) : null}
        <ListItemText primary={primary} />
        {secondaryIcon ? <span className={classes.secondaryIcon}>{secondaryIcon}</span> : null}
      </ListItem>
    </li>
  );
}

const fallbackMenuItems = [
  { type: "builtin", builtinKey: "dashboard", label: "Dashboard", icon: "📊", path: "/", section: "main", isActive: true, position: 0 },
  { type: "builtin", builtinKey: "connections", label: "Conexiones", icon: "🔗", path: "/connections", section: "main", isActive: true, position: 1 },
  { type: "builtin", builtinKey: "tickets", label: "Chats", icon: "💬", path: "/tickets", section: "main", isActive: true, position: 2 },
  { type: "builtin", builtinKey: "contacts", label: "Contactos", icon: "👥", path: "/contacts", section: "main", isActive: true, position: 3 },
  { type: "builtin", builtinKey: "quickAnswers", label: "Respuestas rápidas", icon: "⚡", path: "/quickAnswers", section: "main", isActive: true, position: 4 },
  { type: "builtin", builtinKey: "reminders", label: "Recordatorios", icon: "⏰", path: "/reminders", section: "main", isActive: true, position: 5 },
  { type: "builtin", builtinKey: "agenda", label: "Agenda", icon: "📅", path: "/appointments", section: "main", isActive: true, position: 6 },
  { type: "builtin", builtinKey: "orders", label: "Pedidos", icon: "🛒", path: "/orders", section: "main", isActive: true, position: 7 },
  { type: "builtin", builtinKey: "processes", label: "Procesos", icon: "📃", path: "/processes", section: "main", isActive: true, position: 8 },
  { type: "builtin", builtinKey: "streaming", label: "Streaming", icon: "📺", path: "/streaming", section: "main", isActive: true, position: 9 },
  { type: "builtin", builtinKey: "campaigns", label: "Campañas", icon: "📢", path: "/campaigns", section: "main", isActive: true, position: 10 },
  { type: "builtin", builtinKey: "internalChat", label: "Chat Interno", icon: "🗨️", path: "/internal-chat", section: "main", isActive: true, position: 11 },
  { type: "builtin", builtinKey: "funnels", label: "Embudos", icon: "📈", path: "/funnels", section: "main", isActive: true, position: 12 },
  { type: "builtin", builtinKey: "chatbot", label: "Chatbot", icon: "🤖", path: "/chatbot", section: "main", isActive: true, position: 13 },
  { type: "builtin", builtinKey: "ai", label: "IA", icon: "🧠", path: "/ai", section: "main", isActive: true, position: 14 },
  { type: "builtin", builtinKey: "sidepanelApps", label: "Apps", icon: "📱", path: "/apps", section: "main", isActive: true, position: 15 },
  { type: "prototype", builtinKey: "workspaceData", label: "Base de datos", icon: "🧱", path: "/workspace", section: "main", isActive: true, position: 16 },
  { type: "builtin", builtinKey: "reseller", label: "Panel Reseller", icon: "🤝", path: "/reseller", section: "main", isActive: true, position: 17 },
  { type: "builtin", builtinKey: "tutorials", label: "Tutoriales", icon: "🎓", path: "/tutorials", section: "main", isActive: true, position: 18 },
  { type: "builtin", builtinKey: "users", label: "Usuarios", icon: "👤", path: "/users", section: "administration", isActive: true, position: 0 },
  { type: "builtin", builtinKey: "queues", label: "Áreas", icon: "🏷️", path: "/queues", section: "administration", isActive: true, position: 1 },
  { type: "builtin", builtinKey: "labels", label: "Etiquetas", icon: "🏷️", path: "/labels", section: "administration", isActive: true, position: 2 },
  { type: "builtin", builtinKey: "webhooks", label: "Webhooks", icon: "🔌", path: "/webhooks", section: "administration", isActive: true, position: 3 },
  { type: "builtin", builtinKey: "menu", label: "Menú", icon: "☰", path: "/menu", section: "administration", isActive: true, position: 4 }
];

const prototypeMenuItem = {
  type: "prototype",
  builtinKey: "workspaceData",
  label: "Base de datos",
  icon: "🧱",
  path: "/workspace",
  section: "main",
  isActive: true,
  position: 16
};

const resellerMenuItem = {
  type: "builtin",
  builtinKey: "reseller",
  label: "Panel Reseller",
  icon: "🤝",
  path: "/reseller",
  section: "main",
  isActive: true,
  position: 17
};

const resellerAllowedMenuOrder = [
  "connections",
  "chatbot",
  "agenda",
  "orders",
  "processes",
  "streaming",
  "contacts",
  "workspaceData",
  "reseller"
];

const withRequiredMenuItems = items => {
  const baseItems = Array.isArray(items) ? items : [];
  const workspaceExists = baseItems.some(
    item => item.path === prototypeMenuItem.path || item.builtinKey === prototypeMenuItem.builtinKey
  );
  const resellerExists = baseItems.some(
    item => item.path === resellerMenuItem.path || item.builtinKey === resellerMenuItem.builtinKey
  );
  let nextItems = workspaceExists ? baseItems : [...baseItems, prototypeMenuItem];

  if (!resellerExists) nextItems = [...nextItems, resellerMenuItem];

  return nextItems;
};

const normalizeMenuItemLabels = item => {
  if (item?.builtinKey === "workspaceData") {
    return {
      ...item,
      label: "Base de datos",
      path: "/workspace"
    };
  }

  if (item?.builtinKey === "reseller") {
    return {
      ...item,
      label: "Panel Reseller",
      path: "/reseller"
    };
  }

  return item;
};

const MainListItems = (props) => {
  const {
    drawerClose,
    drawerOpen = true,
    assignmentMode = "multiagent",
    showAllNormalMenu = false
  } = props;
  const classes = useStyles();
  const { connections } = useContext(ConnectionsContext);
  const { user } = useContext(AuthContext);
  const [connectionWarning, setConnectionWarning] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [menuCategories, setMenuCategories] = useState([]);
  const [categoryMenu, setCategoryMenu] = useState(null);
  const isNormalMode = assignmentMode === "normal";
  const hasResellerBranding = Boolean(user?.company?.resellerBranding);

  useEffect(() => {
    let mounted = true;

    const fetchMenuItems = async () => {
      try {
        const { data } = await api.get("/account-menu");
        if (mounted) {
          setMenuItems(data.items || []);
          setMenuCategories(data.categories || []);
        }
      } catch {
        if (mounted) {
          setMenuItems([]);
          setMenuCategories([]);
        }
      }
    };

    if (user?.id) fetchMenuItems();

    const handleAccountMenuUpdate = event => {
      if (mounted && Array.isArray(event.detail?.items)) {
        setMenuItems(event.detail.items);
        setMenuCategories(event.detail.categories || []);
      }
    };

    window.addEventListener("account-menu-updated", handleAccountMenuUpdate);

    return () => {
      mounted = false;
      window.removeEventListener("account-menu-updated", handleAccountMenuUpdate);
    };
  }, [user?.id]);

  useEffect(() => {
    const companyStatus = String(user?.company?.status || "").toLowerCase();
    const companyExpiresAt = user?.company?.expiresAt;
    const companyExpired =
      Boolean(companyExpiresAt) &&
      new Date(companyExpiresAt).getTime() < Date.now();
    const companyInactive =
      companyStatus === "inactive" ||
      companyStatus === "inactivo" ||
      companyStatus === "inativa" ||
      companyExpired;

    if (companyInactive || !connections.length) {
      setConnectionWarning(false);
      return undefined;
    }

    const delayDebounceFn = setTimeout(() => {
      const hasLinkedConnection = connections.some((whats) => {
        return whats.status === "CONNECTED";
      });

      if (hasLinkedConnection) {
        setConnectionWarning(false);
        return;
      }

      const offlineWhats = connections.filter((whats) => {
        return (
          whats.status === "qrcode" ||
          whats.status === "PAIRING" ||
          whats.status === "DISCONNECTED" ||
          whats.status === "TIMEOUT" ||
          whats.status === "OPENING"
        );
      });

      setConnectionWarning(offlineWhats.length > 0);
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [
    connections,
    user?.company?.status,
    user?.company?.expiresAt,
    user?.company?.maxConnections
  ]);

  const normalizedAccountMenuItems = withRequiredMenuItems(menuItems.length ? menuItems : fallbackMenuItems)
    .map(normalizeMenuItemLabels);
  const accountMenuItems = hasResellerBranding
    ? resellerAllowedMenuOrder
        .map(key =>
          normalizedAccountMenuItems.find(item => item.builtinKey === key)
        )
        .filter(Boolean)
        .map((item, index) => ({
          ...item,
          section: "main",
          categoryId: null,
          position: index
        }))
    : normalizedAccountMenuItems;
  const accountMenuCategories = hasResellerBranding ? [] : menuCategories || [];

  const canRenderBuiltinItem = item => {
    if (!item.isActive) return false;
    if (
      isNormalMode &&
      !showAllNormalMenu &&
      ["internalChat", "users", "queues"].includes(item.builtinKey)
    ) {
      return false;
    }
    return hasMenuPermission(user, item.builtinKey);
  };

  const canRenderItem = item => {
    if (!item.isActive) return false;
    if (item.builtinKey) return canRenderBuiltinItem(item);
    return true;
  };

  const getItemTarget = item => {
    if (item.type === "external") return { externalUrl: item.url || "#" };
    if (item.type === "embed") return { to: `/menu-embed/${item.id}` };
    return { to: item.path || "/" };
  };

  const renderMenuItem = item => {
    if (!canRenderItem(item)) return null;
    const target = getItemTarget(item);
    const showWarning =
      item.type === "builtin" && ["connections", "tickets"].includes(item.builtinKey);
    const icon = (
      <MenuEmoji label={item.label}>
        {item.icon || "🔗"}
      </MenuEmoji>
    );

    return (
      <ListItemLink
        key={`${item.type}-${item.id || item.builtinKey}`}
        {...target}
        primary={item.label}
        icon={
          showWarning ? (
            <Badge badgeContent={connectionWarning ? "!" : 0} color="error">
              {icon}
            </Badge>
          ) : icon
        }
        secondaryIcon={item.type === "external" ? <LaunchIcon fontSize="small" /> : null}
        drawerOpen={drawerOpen}
      />
    );
  };

  const renderCategoryMenuItem = item => {
    if (!canRenderItem(item)) return null;
    const target = getItemTarget(item);
    const content = (
      <>
        <span style={{ marginRight: 8 }}>{item.icon || "🔗"}</span>
        {item.label}
        {item.type === "external" ? <LaunchIcon fontSize="small" style={{ marginLeft: 8 }} /> : null}
      </>
    );

    if (target.externalUrl) {
      return (
        <MenuItem
          key={`${item.type}-${item.id || item.builtinKey}`}
          component="a"
          href={target.externalUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setCategoryMenu(null)}
        >
          {content}
        </MenuItem>
      );
    }

    return (
      <MenuItem
        key={`${item.type}-${item.id || item.builtinKey}`}
        component={RouterLink}
        to={target.to}
        onClick={() => setCategoryMenu(null)}
      >
        {content}
      </MenuItem>
    );
  };

  const renderMenuCategory = category => {
    if (!category?.isActive) return null;
    const children = accountMenuItems
      .filter(item => Number(item.categoryId) === Number(category.id))
      .filter(canRenderItem);

    if (!children.length) return null;

    const open = Number(categoryMenu?.id) === Number(category.id);

    return (
      <React.Fragment key={`category-${category.id}`}>
        <li>
          <ListItem
            button
            className={classes.listItem}
            onClick={event => {
              event.stopPropagation();
              setCategoryMenu({ id: category.id, anchorEl: event.currentTarget });
            }}
          >
            <ListItemIcon
              className={`${classes.listItemIcon} ${
                drawerOpen ? "" : classes.listItemIconClosed
              }`}
            >
              <span
                className={classes.categoryColor}
                style={{ backgroundColor: category.color || "#2F80ED" }}
              />
            </ListItemIcon>
            <ListItemText primary={category.name} />
          </ListItem>
        </li>
        <Menu
          anchorEl={categoryMenu?.anchorEl}
          open={open}
          onClose={() => setCategoryMenu(null)}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: "center", horizontal: "right" }}
          transformOrigin={{ vertical: "center", horizontal: "left" }}
        >
          {children.map(renderCategoryMenuItem)}
        </Menu>
      </React.Fragment>
    );
  };

  const mainItems = accountMenuItems.filter(item => item.section === "main" && !item.categoryId);
  const mainCategories = accountMenuCategories.filter(category => category.section === "main");
  const administrationItems = accountMenuItems.filter(
    item => item.section === "administration" && !item.categoryId
  );
  const administrationCategories = accountMenuCategories.filter(
    category => category.section === "administration"
  );

  return (
    <div onClick={drawerClose}>
      {mainItems.map(renderMenuItem)}
      {mainCategories.map(renderMenuCategory)}
      {!hasResellerBranding && (
        <>
          <Can
            role={user.profile}
            perform="drawer-superadmin-items:view"
            yes={() => (
              <>
                <Divider />
                <ListSubheader inset className={classes.listSubheader}>
                  👑 {i18n.t("mainDrawer.listItems.superAdmin")}
                </ListSubheader>
                <ListItemLink
                  to="/superadmin"
                  primary={i18n.t("mainDrawer.listItems.accounts")}
                  icon={<MenuEmoji label="Cuentas">🏢</MenuEmoji>}
                  drawerOpen={drawerOpen}
                />
                <ListItemLink
                  to="/monitoring"
                  primary="Monitoring"
                  icon={<MenuEmoji label="Monitoring">📡</MenuEmoji>}
                  drawerOpen={drawerOpen}
                />
                {hasMenuPermission(user, "settings") && (
                  <ListItemLink
                    to="/settings"
                    primary={i18n.t("mainDrawer.listItems.settings")}
                    icon={<MenuEmoji label="Configuración">⚙️</MenuEmoji>}
                    drawerOpen={drawerOpen}
                  />
                )}
              </>
            )}
          />
          <Can
            role={user.profile}
            perform="drawer-admin-items:view"
            yes={() => (
              <>
                <Divider />
                <ListSubheader inset className={classes.listSubheader}>
                  🛠️ {i18n.t("mainDrawer.listItems.administration")}
                </ListSubheader>
                {administrationItems.map(renderMenuItem)}
                {administrationCategories.map(renderMenuCategory)}
              </>
            )}
          />
        </>
      )}
    </div>
  );
};

export default MainListItems;

